// Lib
import { isEmpty } from 'lodash/fp';

// Utils
import { getLocalTimezone } from '../../../common/utils/timeUtil';
import { manuallyReportError } from '../../analytics/rollbarService';
import { isFeatureEnabled } from '../../../common/users/utils/userPropertyUtils';
import { buildBoardSummaries } from '../../../common/elements/utils/boardSummaryUtils';
import { buildBoardHierarchies } from '../../../common/elements/utils/boardHierarchyUtils';

// Helpers
import { getId } from '../../../common/users/userHelper';

// Selectors
import { getElements } from '../../element/selectors/elementsSelector';
import { getCurrentUser, isGuestSelector } from '../../user/currentUserSelector';
import { getIsLocalCacheHydrated, getLocalCacheHydrationTimestamp } from '../../offline/cache/localCacheSelector';
import {
    getAppInitTimestamp,
    getCurrentUserInitialisationTimestamp,
    isAppInitCompleted,
    isCurrentUserInitialised,
} from '../../app/initialisation/initialisationSelector';

// Actions
import { appSetInitialised } from '../../app/initialisation/initialisationActions';
import {
    fetchCurrentUser,
    fetchCurrentUserInitialQuickSearchBoardSummaries,
    initialiseAppForCurrentUser,
    setCurrentUserGuest,
} from '../../user/currentUserActions';
import { fetchNotifications } from '../../notifications/notificationActions';
import { openErrorNotice } from '../../components/error/errorNoticeActions';
import { boardSummariesLoad } from '../../element/board/summary/boardSummariesActions';
import { boardHierarchiesLoad } from '../../element/board/hierarchy/boardHierarchiesActions';

// Constants
import { USER_ERROR } from '../../../common/error/errorConstants';
import { ExperimentId } from '../../../common/experiments/experimentsConstants';

/**
 * Prepares the board summaries based on the initially retrieved elements.
 */
const initialiseBoardSummariesAndHierarchies = (dispatch: Function, getState: Function) => {
    const state = getState();
    const elements = getElements(state);

    const boardSummaries = buildBoardSummaries(elements);
    const boardHierarchies = buildBoardHierarchies(elements);

    if (!isEmpty(boardSummaries)) {
        dispatch(boardSummariesLoad(boardSummaries));
    }
    if (!isEmpty(boardHierarchies)) {
        dispatch(boardHierarchiesLoad(boardHierarchies));
    }
};

/**
 * Orchestrates the initialisation of the Milanote workspace.
 */
const initialiseAuthenticatedUserWorkspace =
    () =>
    async (dispatch: Function, getState: Function): Promise<void> => {
        try {
            const [appInitResponse] = await Promise.all([
                dispatch(initialiseAppForCurrentUser(getLocalTimezone)),
                dispatch(fetchNotifications({ after: 0 })),
            ]);

            const state = getState();

            const currentUser = getCurrentUser(state);

            const usingGraphInitialisation = isFeatureEnabled(ExperimentId.graphDbAppInit, currentUser);

            if (usingGraphInitialisation) {
                await dispatch(fetchCurrentUserInitialQuickSearchBoardSummaries());
            }

            initialiseBoardSummariesAndHierarchies(dispatch, getState);

            dispatch(
                appSetInitialised({
                    ...appInitResponse.initialised,
                    boardSummaries: true,
                }),
            );
        } catch (err: any) {
            manuallyReportError({
                error: err,
                errorMessage: 'App Init request failed',
            });

            dispatch(openErrorNotice({ errorNoticeId: USER_ERROR.APP_INIT_ERROR }));
        }
    };

/**
 * This is called whenever the workspace mounts or the user's logged in state changes.
 */
export const initialiseWorkspace = () => (dispatch: Function, getState: Function) => {
    const state = getState();

    const isGuest = isGuestSelector(state);

    if (isGuest) return dispatch(setCurrentUserGuest());

    const isLocalCacheHydrated = getIsLocalCacheHydrated(state);
    const localCacheHydrationTimestamp = getLocalCacheHydrationTimestamp(state);

    // Initialise the user if they're not already initialised, or they were initialised in the previous session
    const shouldInitialiseCurrentUser =
        !isCurrentUserInitialised(state) ||
        (isLocalCacheHydrated && localCacheHydrationTimestamp > getCurrentUserInitialisationTimestamp(state));

    if (shouldInitialiseCurrentUser) dispatch(fetchCurrentUser());

    // Initialise the app if it's not already initialised, or it was initialised in the previous session
    const shouldInitialiseApplicationData =
        !isAppInitCompleted(state) ||
        (isLocalCacheHydrated && localCacheHydrationTimestamp > getAppInitTimestamp(state));

    if (shouldInitialiseApplicationData) dispatch(initialiseAuthenticatedUserWorkspace());
};
